import React from 'react';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppSelector } from 'flow/hooks/useRedux';
import {
    selectActiveMeetingScreenShareMemberName,
    selectViewerIsInternal,
} from 'flow/selectors/meeting-selectors';
import Button from 'flow/components/ui-components/Button';
import Icon from 'flow/components/ui-components/Icon';
import useMeetingMembers, { MeetingMember } from './useMeetingMembers';
import useWaitingParticipants, {
    WaitingParticipant,
} from './useWaitingParticipants';
import useAddParticipant from './useAddParticipant';
import { getRoomHostControlsVisibility } from './roomHostControls';
import MeetingParticipantsListHeader from './MeetingParticipantsListHeader';
import MeetingParticipantRow from './MeetingParticipantRow';
import MeetingGuestParticipantRow from './MeetingGuestParticipantRow';
import MeetingCallingParticipantRow from './MeetingCallingParticipantRow';
import MeetingWaitingParticipantRow from './MeetingWaitingParticipantRow';
import MeetingWaitingSectionMenu from './MeetingWaitingSectionMenu';
import MeetingAllParticipantsMenu from './MeetingAllParticipantsMenu';

interface MeetingParticipantsFooterProps {
    anyOtherAudioActive: boolean;
    anyOtherVideoActive: boolean;
    everyoneAudioInactive: boolean;
    everyoneVideoInactive: boolean;
}

const MeetingParticipantsFooter = ({
    anyOtherAudioActive,
    anyOtherVideoActive,
    everyoneAudioInactive,
    everyoneVideoInactive,
}: MeetingParticipantsFooterProps) => {
    const { i18next } = useAppContext();
    const { canInviteParticipant, onAddParticipant } = useAddParticipant();
    const viewerIsInternal = useAppSelector(selectViewerIsInternal);

    return (
        <div className="flex items-center gap-2 px-6 py-4">
            {canInviteParticipant && (
                <Button
                    className="flex h-12 flex-1 items-center justify-center gap-3 !rounded-xl !bg-gray-700 !text-white hover:!bg-gray-600"
                    onClick={onAddParticipant}
                >
                    <Icon iconKey="add" />
                    <span>
                        {i18next.t(
                            'call.canvas.videoparticipants.addselectedparticipant.title',
                        )}
                    </span>
                </Button>
            )}
            {viewerIsInternal && (
                <div className="ms-auto">
                    <MeetingAllParticipantsMenu
                        anyOtherAudioActive={anyOtherAudioActive}
                        anyOtherVideoActive={anyOtherVideoActive}
                        everyoneAudioInactive={everyoneAudioInactive}
                        everyoneVideoInactive={everyoneVideoInactive}
                    />
                </div>
            )}
        </div>
    );
};

const MeetingGuestSection = ({
    guests,
    lazyLoad,
    showDivider,
}: {
    guests: MeetingMember[];
    lazyLoad: boolean;
    // Only meaningful above a preceding section (room members or calling
    // rows) — without one, the rule would sit above nothing.
    showDivider: boolean;
}) => {
    const { i18next } = useAppContext();

    return (
        <>
            {showDivider && <hr className="mt-4 border-t border-gray-700" />}
            <div className="px-6 pt-6 pb-2 text-base font-bold">
                {i18next.t('meetings.participants.guests_header')}
            </div>
            {guests.map((member) => (
                <MeetingGuestParticipantRow
                    key={member.memberKey}
                    member={member}
                    lazyLoad={lazyLoad}
                />
            ))}
        </>
    );
};

/**
 * The people asking to join, above everyone already here (VID-76): this is the
 * only section of the panel that wants something from the reader, so it leads.
 * Rendered only while somebody is actually waiting — an empty heading would
 * just be noise in a panel that is usually all roster — and only for an
 * internal participant, who is the only one allowed to decide (see
 * selectViewerIsInternal). A guest is not shown the queue at all rather than
 * shown it without the buttons: it is not theirs to act on or to know about.
 */
const MeetingWaitingSection = ({
    waitingParticipants,
}: {
    waitingParticipants: WaitingParticipant[];
}) => {
    const { i18next } = useAppContext();

    return (
        <>
            {/* justify-between rather than a margin on the menu, so the kebab
                lands on the trailing edge in both reading directions. */}
            <div className="flex items-center justify-between px-2 pt-2 pb-2">
                <span className="text-base font-bold">
                    {i18next.t('videoconference.waiting_room.asking_to_join')}
                </span>
                <MeetingWaitingSectionMenu />
            </div>
            {waitingParticipants.map((participant) => (
                <MeetingWaitingParticipantRow
                    key={participant.name}
                    participant={participant}
                />
            ))}
            <hr className="my-3 border-t border-gray-700" />
        </>
    );
};

const MeetingParticipantsList = () => {
    const { i18next } = useAppContext();
    const {
        members,
        roomMembers,
        guestMembers,
        callingMembers,
        count,
        closeMembersList,
    } = useMeetingMembers();
    const waitingParticipants = useWaitingParticipants();
    const viewerIsInternal = useAppSelector(selectViewerIsInternal);

    const lazyLoad = members.length > 10;
    const hasMembers = members.length > 0;

    const screenShareMemberName = useAppSelector(
        selectActiveMeetingScreenShareMemberName,
    );

    // Room-wide host controls (VID-53 AC2): shown per applicable track, excluding
    // self, audio-only callers, and the active screen-sharer. See roomHostControls.
    const {
        anyOtherAudioActive,
        anyOtherVideoActive,
        everyoneAudioInactive,
        everyoneVideoInactive,
    } = getRoomHostControlsVisibility(members, screenShareMemberName);

    // The card fills the panel column; MeetingSidePanel frames it (the gap to
    // the video / the viewport edges), so the card carries no margin itself.
    return (
        <div className="badge-bg flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl bg-gray-800 text-white">
            <MeetingParticipantsListHeader
                count={count}
                onClose={closeMembersList}
            />
            <div className="flex-1 overflow-auto px-2 pb-2">
                {viewerIsInternal && waitingParticipants.length > 0 && (
                    <MeetingWaitingSection
                        waitingParticipants={waitingParticipants}
                    />
                )}
                {hasMembers ? (
                    <>
                        {roomMembers.map((member) => (
                            <MeetingParticipantRow
                                key={member.memberKey}
                                member={member}
                                lazyLoad={lazyLoad}
                            />
                        ))}
                        {callingMembers.map((member) => (
                            <MeetingCallingParticipantRow
                                key={member.key}
                                member={member}
                            />
                        ))}
                        {guestMembers.length > 0 && (
                            <MeetingGuestSection
                                guests={guestMembers}
                                lazyLoad={lazyLoad}
                                showDivider={
                                    roomMembers.length > 0 ||
                                    callingMembers.length > 0
                                }
                            />
                        )}
                    </>
                ) : (
                    <div className="p-3">
                        {i18next.t('videoconference.general.no_participants')}
                    </div>
                )}
            </div>
            <MeetingParticipantsFooter
                anyOtherAudioActive={anyOtherAudioActive}
                anyOtherVideoActive={anyOtherVideoActive}
                everyoneAudioInactive={everyoneAudioInactive}
                everyoneVideoInactive={everyoneVideoInactive}
            />
        </div>
    );
};

export default MeetingParticipantsList;
